import React, { useState } from 'react';
import { Box, Typography, Button, Grid, TextField, Paper, Grow, FormControlLabel, Checkbox, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigation } from 'Hooks/navigate';
import EILOGO from 'Assets/Images/favicon.ico';
import ToastSnackbar from 'Common/SnackbarToast';
import API from 'api';

const ContactUsSection = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #0f172a, #1e293b)',
    color: '#fff',
    padding: theme.spacing(5.5),
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    position: 'relative',
}));

const ContactForm = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
}));

// Email validation regex pattern
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Yup validation schema
const contactUsValidationSchema = Yup.object().shape({
    email: Yup.string()
        .required('Email is required')
        .matches(emailRegex, 'Please enter a valid email address')
        .test('email-format', 'Email must be a valid business/work email', function(value) {
            if (!value) return false;
            const parts = value.split('@');
            if (parts.length !== 2) return false;
            const [localPart, domain] = parts;
            // Check local part length and valid characters
            if (localPart.length === 0 || localPart.length > 64) return false;
            if (!/^[a-zA-Z0-9._%-]+$/.test(localPart)) return false;
            // Check domain has at least one dot and valid format
            if (!domain.includes('.')) return false;
            if (!/^[a-zA-Z0-9.-]+$/.test(domain)) return false;
            // Check TLD is at least 2 characters
            const tld = domain.split('.').pop();
            if (!tld || tld.length < 2) return false;
            return true;
        }),
    firmName: Yup.string()
        .required('Firm name is required')
        .min(2, 'Firm name must be at least 2 characters'),
    automation: Yup.string()
        .max(1000, 'Automation details cannot exceed 1000 characters')
        .min(5, 'Please provide more details (at least 5 characters)'),
    connectToMarketing: Yup.boolean(),
});

const ContactUs = () => {
    const { goBack, goHome } = useNavigation();
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [loading, setLoading] = useState(false);

    const initialVals = {
            email: '',
            firmName: '',
            automation: '',
            connectToMarketing: true,
        }

    // Formik initialization
    const formik = useFormik({
        initialValues: initialVals,
        validationSchema: contactUsValidationSchema,
        onSubmit: async (values) => {
            setLoading(true);

            try {
                const response = await API.contactUs.submit(values);

                if (response.data.success) {
                    setSnackbar({
                        open: true,
                        message: response.data.message || 'Thanks! We will reach out shortly.',
                        severity: 'success'
                    });

                    // Reset form
                    formik.resetForm();
                }
            } catch (error) {
                console.error('Contact form submission error:', error);
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <Box sx={{
            background: "linear-gradient(to right, #041e42, #78a8e6)",
            minHeight: "100vh",
            color: "white",
            display: "flex",
            alignItems: "center",
            py: 8,
            justifyContent: "center",
        }}>
            <Box id="contact" sx={{ my: 4, maxWidth: "60%", m: "auto" }}>
                <Grow in timeout={1200}>
                    <ContactUsSection>
                        <Box sx={{ position: 'absolute', top: 25, right: 25 }}>
                            <img
                                src={EILOGO}
                                alt="Hero Art"
                                style={{ width: '70px', height: '70px', cursor: "pointer" }}
                                onClick={() => goHome()}
                            />
                        </Box>
                        <Typography variant="h3" component="h2" gutterBottom>
                            Get in touch
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Tell us about your requirements for a follow up discussion.
                        </Typography>
                    </ContactUsSection>
                </Grow>
                <Grow in timeout={1500}>
                    <ContactForm component="form" onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Work Email"
                                    type="email"
                                    name="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="you@firm.com"
                                    required
                                    disabled={loading}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Firm Name"
                                    name="firmName"
                                    value={formik.values.firmName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Your firm"
                                    required
                                    disabled={loading}
                                    error={formik.touched.firmName && Boolean(formik.errors.firmName)}
                                    helperText={formik.touched.firmName && formik.errors.firmName}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Contact Request Description"
                                    multiline
                                    rows={4}
                                    name="automation"
                                    value={formik.values.automation}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Contact Request Description"
                                    disabled={loading}
                                    error={formik.touched.automation && Boolean(formik.errors.automation)}
                                    helperText={formik.touched.automation && formik.errors.automation}
                                    inputProps={{ maxLength: 1000 }}
                                />
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Typography variant="caption" color={formik.values.automation.length > 900 ? '#ff9800' : '#999'}>
                                        {formik.values.automation.length}/1000
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ mt: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name='connectToMarketing'
                                                checked={formik.values.connectToMarketing}
                                                onChange={formik.handleChange}
                                                disabled={loading}
                                            />
                                        }
                                        label="I would like to hear from Evolving Intelligence Inc. about applied AI for Private Wealth organizations and other Evolving Intelligence Inc. platforms, services, and events, including the latest solutions, tips, and exclusive offers."
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sx={{ display: "flex", gap: 1, justifyContent: "right" }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        backgroundColor: '#334155',
                                        '&:hover': { backgroundColor: '#1e293b' },
                                    }}
                                    onClick={() => goBack()}
                                    disabled={loading}
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        backgroundColor: '#2563eb',
                                        '&:hover': { backgroundColor: '#1d4ed8' },
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? 'Submitting...' : 'Submit'}
                                </Button>
                            </Grid>
                        </Grid>
                    </ContactForm>
                </Grow>
            </Box>
            {/* Custom Toast Snackbar */}
            {snackbar.open && (
                <ToastSnackbar snackbar={snackbar} />
            )}
        </Box>
    )
}

export default ContactUs;